import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, Grid, Typography, MenuItem, Select, InputLabel, FormControl, IconButton, Divider, Box } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import axios from '../axios';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CloseIcon from '@mui/icons-material/Close';

const AlunnoModal = ({ open, onClose, onSave, alunno, setAlunno }) => {
    const [errors, setErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [corsi, setCorsi] = useState([]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAlunno(prevAlunno => ({ ...prevAlunno, [name]: value }));
        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: null }));
        }
    };

    const handleYearChange = (date) => {
        setAlunno(prevAlunno => ({ ...prevAlunno, anno_di_nascita: date ? date.year() : '' }));
        if (errors.anno_di_nascita) {
            setErrors(prevErrors => ({ ...prevErrors, anno_di_nascita: null }));
        }
    };

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateFields = () => {
        const newErrors = {};
        if (!alunno.first_name) newErrors.first_name = 'Campo Obbligatorio';
        if (!alunno.last_name) newErrors.last_name = 'Campo Obbligatorio';
        if (!alunno.uemail) {
            newErrors.uemail = 'Campo Obbligatorio';
        } else if (!isValidEmail(alunno.uemail)) {
            newErrors.uemail = 'Indirizzo email non valido';
        }
        if (!alunno.course_id) newErrors.seleziona_corso = 'Campo Obbligatorio';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        const fetchCorsi = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.post('/datahandler/corsi-insegnante', { token });
                setCorsi(response.data);
            } catch (error) {
                console.error('Error fetching courses:', error);
            }
        };
        fetchCorsi();
    }, []);

    useEffect(() => {
        setIsFormValid(validateFields());
    }, [alunno]);

    const handleSave = () => {
        if (validateFields()) {
            onSave();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <AccountCircleIcon />
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {alunno.id ? 'Modifica Alunno' : 'Crea Nuovo Alunno'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 4 }}>
                {/* Dati anagrafici */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{
                        fontWeight: 700,
                        color: '#F57C00',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                    }}>
                        <AccountCircleIcon fontSize="small" />
                        Dati anagrafici
                    </Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nome alunno *"
                                name="first_name"
                                fullWidth
                                value={alunno.first_name}
                                onChange={handleChange}
                                error={!!errors.first_name}
                                helperText={errors.first_name}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Cognome alunno *"
                                name="last_name"
                                fullWidth
                                value={alunno.last_name}
                                onChange={handleChange}
                                error={!!errors.last_name}
                                helperText={errors.last_name}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    views={['year']}
                                    label="Anno di nascita"
                                    value={alunno.anno_di_nascita ? moment().year(alunno.anno_di_nascita) : null}
                                    onChange={handleYearChange}
                                    maxDate={moment()}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: 'outlined',
                                            error: !!errors.anno_di_nascita,
                                            helperText: errors.anno_di_nascita
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nome e cognome genitore di riferimento"
                                name="nome_e_cognome_genitore"
                                fullWidth
                                value={alunno.nome_e_cognome_genitore}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 3, opacity: 0.6 }} />

                {/* Dati di accesso */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{
                        fontWeight: 700,
                        color: '#673ab7',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                    }}>
                        <VpnKeyIcon fontSize="small" />
                        Dati di accesso al DinoClub
                    </Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="E-mail *"
                                name="uemail"
                                type="email"
                                fullWidth
                                value={alunno.uemail}
                                onChange={handleChange}
                                error={!!errors.uemail}
                                helperText={errors.uemail}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!errors.seleziona_corso}>
                                <InputLabel>Seleziona Corso *</InputLabel>
                                <Select
                                    label="Seleziona Corso *"
                                    name="course_id"
                                    value={alunno.course_id || ''}
                                    onChange={handleChange}
                                >
                                    {corsi.map((corso) => (
                                        <MenuItem key={corso.ID} value={corso.ID}>
                                            {corso.post_title}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.seleziona_corso && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                        {errors.seleziona_corso}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 3, opacity: 0.6 }} />

                {/* Dati fatturazione */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{
                        fontWeight: 700,
                        color: '#2e7d32',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                    }}>
                        <ReceiptLongIcon fontSize="small" />
                        Dati fatturazione
                    </Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Codice fiscale alunno"
                                name="codice_fiscale"
                                fullWidth
                                value={alunno.codice_fiscale}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Indirizzo"
                                name="indirizzo_fatturazione"
                                fullWidth
                                value={alunno.indirizzo_fatturazione}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="CAP"
                                name="cap_fatturazione"
                                fullWidth
                                value={alunno.cap_fatturazione}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Città"
                                name="citta_fatturazione"
                                fullWidth
                                value={alunno.citta_fatturazione}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Telefono"
                                name="telefono_fatturazione"
                                fullWidth
                                value={alunno.telefono_fatturazione}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 3, opacity: 0.6 }} />

                {/* Dati per comunicazioni */}
                <Box>
                    <Typography variant="subtitle2" sx={{
                        fontWeight: 700,
                        color: '#0277bd',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                    }}>
                        <ContactMailIcon fontSize="small" />
                        Dati per comunicazioni
                    </Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nome e cognome padre"
                                name="nome_e_cognome_padre"
                                fullWidth
                                value={alunno.nome_e_cognome_padre}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Telefono padre"
                                name="telefono_padre"
                                fullWidth
                                value={alunno.telefono_padre}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nome e cognome madre"
                                name="nome_e_cognome_madre"
                                fullWidth
                                value={alunno.nome_e_cognome_madre}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Telefono madre"
                                name="telefono_madre"
                                fullWidth
                                value={alunno.telefono_madre}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                label="E-mail per comunicazioni"
                                name="email_per_comunicazioni"
                                type="email"
                                fullWidth
                                value={alunno.email_per_comunicazioni}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, background: 'rgba(0,0,0,0.02)', gap: 2 }}>
                <Button
                    variant="contained"
                    onClick={onClose}
                    className="btn-3d btn-3d-grey"
                    sx={{ px: 4 }}
                >
                    Annulla
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    className={`btn-3d ${isFormValid ? 'btn-3d-orange' : 'btn-3d-grey'}`}
                    disabled={!isFormValid}
                    sx={{
                        px: 6,
                        '&.Mui-disabled': {
                            backgroundColor: '#ccc',
                            color: '#888',
                            boxShadow: '0 4px 0 #999'
                        }
                    }}
                >
                    Salva
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AlunnoModal;
